/**
 * Screen-scoped design tokens for Settings → My Plan → **"Choose your plan"**
 * (the upgrade / plan-picker view).
 *
 * Every colour, type step, radius, border width, spacing and size below is
 * lifted verbatim from the design (`Settings.dc.html`, plan upgrade section) so
 * the components never carry a raw literal. These stay screen-local on purpose
 * — once every screen is re-skinned the shared values get promoted into the MUI
 * theme and this file is deleted.
 */

import {
  button,
  contentCard,
  focusRing,
  formField,
  inkfree,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const upgradePlanTokens = {
  color: {
    /** Deep brand ink — plan name, price, bullet copy. */
    ink: signer.ink,
    /** Secondary copy — back link, card sub-lines, blurbs. */
    body: signer.text.meta,
    /** Muted copy — price unit, billing-cycle note. */
    muted: signer.text.faint,
    surface: white,
    /** Card chrome. */
    cardBorder: contentCard.borderMuted,
    /** Outlined control border (seat stepper, neutral CTA). */
    controlBorder: formField.border,
    /** Hairline between the seat stepper's three cells. */
    stepperDivider: signer.border.light,
    /** Seat stepper button hover fill. */
    stepperHoverBg: signer.surface.muted,
    /** Keyboard focus ring, matching the one used across the app. */
    focusRing,
    /** Highlighted-card ring / link text+hover — a thin accent, stays orange. */
    accent: signer.orange.main,
    accentHover: signer.orange.dark,
    /**
     * Primary action buttons and the "RECOMMENDED" badge — solid fills, so
     * per the standing brand-colour rule these are the app brand red, not
     * `accent` above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** Billing-cycle segmented control. */
    toggleTrackBg: button.disabledBg,
    toggleActiveBg: white,
    toggleActiveText: signer.ink,
    toggleIdleText: signer.text.meta,
    /** "SAVE 25%" badge on the annual segment. */
    saveBadgeText: notification.success.icon,
    saveBadgeBg: notification.success.bg,
    /** Feature-list check mark. */
    bulletCheck: notification.success.icon,
    /** Seat-rule pill ("Up to 5 users"). */
    rulePillText: signer.orange.dark,
    rulePillBg: inkfree.light,
    /** "N seats × $X = $Y/mo" badge. */
    totalNoteBg: formField.bg,
    totalNoteBorder: signer.border.light,
    /** Non-actionable CTA (current plan / not available). */
    inactiveCtaText: signer.text.faint,
    inactiveCtaBorder: signer.border.light,
    /** Why a plan can't be picked. */
    ineligibleNote: notification.error.icon,
    /**
     * Availability chip ("CURRENT PLAN" / "AVAILABLE FOR 8 SEATS" / "NOT
     * AVAILABLE…"). From `Plan & Billing - Promotions.dc.html`; the mock's
     * #5B3FA0/#EFE9FA, #5E5873 and #F1F0F5 fold onto the existing purple
     * chip, meta text and neutral pill tokens.
     */
    chip: {
      current: { text: signer.purple.main, bg: signer.purple.chipBg },
      available: { text: "#B8340F", bg: "#FDEDE6" }, // 5.6:1
      unavailable: { text: signer.text.meta, bg: signer.surface.pill },
    },
    /** Thumb of the feature-list scrollbar. */
    scrollbarThumb: switchControl.trackOff,
  },

  font: {
    backLink: "12.5px",
    cardTitle: "13.5px",
    cardSub: "12px",
    stepperValue: "14px",
    stepperControl: "16px",
    toggle: "12.5px",
    saveBadge: "9.5px",
    seatCta: "13px",
    recommended: "9.5px",
    planName: "15px",
    planPrice: "26px",
    planPer: "11.5px",
    planCycleNote: "10.5px",
    planBlurb: "11.5px",
    totalNote: "11px",
    rulePill: "10.5px",
    chip: "9.5px",
    bullet: "12px",
    planCta: "12.5px",
    planNote: "10.5px",
    compareLink: "12.5px",
  },

  weight: {
    regular: 400,
    medium: 600,
    strong: 700,
    heading: 800,
  },

  letterSpacing: {
    heading: "-0.01em",
    price: "-0.02em",
    recommended: "0.08em",
    rulePill: "0.05em",
  },

  lineHeight: {
    /** Everything the design leaves at the UA default. */
    tight: "normal",
    blurb: 1.5,
    bullet: 1.45,
  },

  radius: {
    card: "14px",
    control: "9px",
    toggleTrack: "10px",
    toggleItem: "7px",
    totalNote: "7px",
    pill: "999px",
  },

  border: {
    hairline: "1px",
    emphasis: "1.5px",
  },

  space: {
    /** Vertical rhythm between the stacked blocks of the upgrade view. */
    section: "20px",
    backLinkGap: "7px",
    cardPaddingY: "18px",
    cardPaddingX: "24px",
    cardGap: "16px",
    cardTextGap: "2px",
    toggleTrackPadding: "4px",
    toggleTrackGap: "4px",
    toggleItemPaddingX: "20px",
    toggleAnnualGap: "8px",
    saveBadgePaddingY: "2px",
    saveBadgePaddingX: "8px",
    seatCtaPaddingX: "20px",
    planGridGap: "14px",
    planGridTop: "6px",
    planCardPadding: "22px 20px 20px",
    planCardGap: "12px",
    planHeadGap: "6px",
    planPriceGap: "4px",
    recommendedPaddingY: "4px",
    recommendedPaddingX: "10px",
    totalNotePaddingY: "4px",
    totalNotePaddingX: "9px",
    rulePillPaddingY: "4px",
    rulePillPaddingX: "11px",
    bulletGap: "8px",
    bulletIconGap: "8px",
    bulletIconTop: "3px",
    planFooterGap: "6px",
    compareLinkGap: "6px",
    compareLinkPaddingY: "4px",
  },

  size: {
    backIcon: 13,
    backIconStroke: 2.5,
    stepperButton: "38px",
    stepperValueWidth: "52px",
    toggleItemHeight: "32px",
    seatCtaHeight: "40px",
    /** Offset that floats the RECOMMENDED pill over the card border. */
    recommendedTop: "-10px",
    recommendedLeft: "18px",
    /**
     * Fixed plan-column width — one quarter of the 920px settings content
     * column minus the three 14px gutters. Fixing it (instead of stretching)
     * keeps a card the design's size no matter how many plans come back, and
     * lets the row centre itself.
     */
    planCardWidth: "219.5px",
    bulletIcon: 11,
    bulletIconStroke: 3,
    /** Feature lists taller than this scroll instead of stretching the card. */
    bulletListMaxHeight: "150px",
    scrollbarWidth: "4px",
    planCtaHeight: "38px",
    compareIcon: 12,
    compareIconStroke: 2,
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
    toggleActive: "0 1px 3px rgba(33, 21, 71, 0.12)",
  },

  /** Cards the current team size has outgrown are dimmed rather than hidden. */
  opacity: {
    ineligibleCard: 0.55,
  },


  /** Seat stepper bounds (design clamps the counter to 1…50). */
  seats: {
    min: 1,
    max: 50,
  },
} as const;

export default upgradePlanTokens;
